import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import type { ArrayHelpers, FormikHelpers, FormikProps } from "formik";
import { FieldArray, Formik } from "formik";
import type { FormEvent, ReactNode } from "react";
import React from "react";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { useCompanyStoreMails } from "~/hooks/useCompanyStoreMails";
import type {
	MailCreateParam,
	MailsCreateParams
} from "~/models/CompanyStoreMail";
import { validationSchemaCompanyStoreMail } from "~/models/CompanyStoreMail";
import { isSuccess } from "~/utils/api/api_handler";
import { create as createMails } from "~/utils/api/CompanyStoreMails";
import { generateKey } from "~/utils/RandomKeyGenerator";
import { CompanyStoreMailModalRow } from "./CompanyStoreMailModalRow";

type Props = {
	handleClose: () => void;
	isOpen: boolean;
};

export const CompanyStoreMailModal: React.FC<Props> = ({
	isOpen,
	handleClose
}: Props) => {
	const { mutate: mutateMails } = useCompanyStoreMails().index();
	const keyId = React.useMemo(() => generateKey(), []);
	const handleSave = async (
		value: MailsCreateParams,
		{ resetForm }: FormikHelpers<MailsCreateParams>
	): Promise<void> => {
		const result = await createMails(value);
		if (isSuccess(result)) {
			await mutateMails?.();
			toast.success("登録しました");
			handleClose();
			resetForm();
			return;
		}
		toast.error("エラーが発生しました");
	};
	return (
		<Formik
			initialValues={{ companyStoreMails: [{ email: "", key: keyId }] }}
			onSubmit={handleSave}
			validationSchema={validationSchemaCompanyStoreMail}
		>
			{({
				values,
				errors,
				handleChange,
				handleReset,
				handleSubmit,
				touched,
				handleBlur
			}: FormikProps<MailsCreateParams>): ReactNode => (
				<Modalv2
					isOpen={isOpen}
					onClose={(): void => {
						handleClose();
						handleReset();
					}}
				>
					<ModalHeader title="メールアドレス追加" />
					<ModalBody>
						<Form
							onSubmit={(e: FormEvent<HTMLFormElement>): void => {
								e.preventDefault();
							}}
						>
							<FieldArray name="companyStoreMails">
								{({ push, remove }: ArrayHelpers): ReactNode => (
									<div>
										{values.companyStoreMails.map(
											(mail: MailCreateParam, index: number) => {
												const mailErrors = errors.companyStoreMails
													? errors.companyStoreMails[index]
													: null;
												return (
													<CompanyStoreMailModalRow
														errors={mailErrors}
														handleBlur={handleBlur}
														handleChange={handleChange}
														index={index}
														key={mail.key}
														remove={remove}
														touched={touched}
														values={values}
													/>
												);
											}
										)}
										<Button
											height="small"
											onClick={(e: React.MouseEvent): void => {
												e.preventDefault();
												const key = generateKey();
												push({ email: "", key });
											}}
											variant="secondary"
										>
											追加
										</Button>
									</div>
								)}
							</FieldArray>
						</Form>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Button onClick={handleClose} variant="secondary">
								保存せず閉じる
							</Button>
						}
						rightArea={
							<Button
								onClick={async (): Promise<void> => {
									handleSubmit();
								}}
								variant="primary"
							>
								保存
							</Button>
						}
					/>
				</Modalv2>
			)}
		</Formik>
	);
};
